import { DateService } from "@/core/services/Date.service";
import { NumberFormatterService } from "@/core/services";
import { NumberFormatType } from "@/core/types";
import {
  IPromoFinancials,
  IPromoListItem,
  IPromoStatusInfo,
  IPromoTypeInfo,
} from "@/core/repositories/promotion";
import { DateAdapter } from "@/infrastructure/adapters/dayJs/DayJs.adapter";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";

export type PromoListItemProps = IPromoListItem;

export class PromoListItem implements IPromoListItem {
  private readonly _promoEventId: number;
  private readonly _promoName: string;
  private readonly _retailerName: string | null;
  private readonly _purchaserName: string | null;
  private readonly _promoStartDate: string;
  private readonly _promoEndDate: string;
  private readonly _type: IPromoTypeInfo;
  private readonly _status: IPromoStatusInfo;
  private readonly _financials: IPromoFinancials;
  private readonly _matchCount: number;
  private readonly _isCurrentlyActive: boolean;

  constructor(props: PromoListItemProps) {
    this._promoEventId = props.promoEventId;
    this._promoName = props.promoName;
    this._retailerName = props.retailerName;
    this._purchaserName = props.purchaserName;
    this._promoStartDate = props.promoStartDate;
    this._promoEndDate = props.promoEndDate;
    this._type = props.type;
    this._status = props.status;
    this._financials = props.financials;
    this._matchCount = props.matchCount;
    this._isCurrentlyActive = props.isCurrentlyActive;
  }

  public get promoEventId(): number {
    return this._promoEventId;
  }

  public get promoName(): string {
    return this._promoName;
  }

  public get retailerName(): string | null {
    return this._retailerName;
  }

  public get purchaserName(): string | null {
    return this._purchaserName;
  }

  public get promoStartDate(): string {
    return this._promoStartDate;
  }

  public get promoEndDate(): string {
    return this._promoEndDate;
  }

  public get type(): IPromoTypeInfo {
    return this._type;
  }

  public get status(): IPromoStatusInfo {
    return this._status;
  }

  public get financials(): IPromoFinancials {
    return this._financials;
  }

  public get matchCount(): number {
    return this._matchCount;
  }

  public get isCurrentlyActive(): boolean {
    return this._isCurrentlyActive;
  }

  public claimedFormatted(format: NumberFormatType): string {
    const claimed = this._financials.previouslyClaimed;
    if (claimed === null || claimed === undefined) {
      return "";
    }
    return new NumberFormatterService(new NumberFormatterAdapter()).format(claimed, format);
  }

  public dateRangeFormatted(format: string): string {
    const dateService = new DateService(new DateAdapter());
    const start = dateService.format(this._promoStartDate, format);
    const end = dateService.format(this._promoEndDate, format);
    return `${start} – ${end}`;
  }

  public displayClaimed(format: NumberFormatType): string {
    const claimed = this._financials.previouslyClaimed;
    if (claimed === null || claimed === undefined || claimed === 0) {
      return "-";
    }
    return this.claimedFormatted(format);
  }

  public displayMatches(): string {
    if (this._matchCount === 0) {
      return "-";
    }
    return String(this._matchCount);
  }
}

export const getBlankPromotions = (count: number): PromoListItem[] => {
  return Array.from(
    { length: count },
    () =>
      new PromoListItem({
        promoEventId: 0,
        promoName: "",
        retailerName: null,
        purchaserName: null,
        promoStartDate: "",
        promoEndDate: "",
        type: {
          promoTypeId: 0,
          promoTypeName: "",
          promoTypeCode: "",
          promoTypeKey: "",
        },
        status: {
          promoStatusId: 0,
          promoStatusName: "",
          promoStatusKey: "",
        },
        financials: {
          committedBudget: null,
          previouslyClaimed: null,
          remainingOpenBalance: null,
        },
        matchCount: 0,
        isCurrentlyActive: false,
      })
  );
};
